/**
 * ChartTooltip — ONE hover bubble for every chart on the Search Detail page.
 *
 * Six charts need hover values (`AdditionsLine`, `FunnelBars`, `LabeledBars`,
 * `StageDonut`, `StageLines`, `OutreachLines`) and before this file they had
 * zero between them. Writing it once is the difference between a consistent
 * dashboard and six near-misses — the failure mode here is not a bug, it is six
 * bubbles that round differently, place differently and go blank on different
 * edge cases.
 *
 * WHY IT IS PINNED TO A CORNER RATHER THAN FOLLOWING THE POINTER. Three reasons,
 * in the order they mattered:
 *
 * 1. A bubble that tracks the pointer needs a computed `left`/`top` every move,
 *    and the design system forbids inline `style={{}}` — the one documented
 *    exception is `DockablePanel`'s drag transform, which this is not.
 * 2. FR1.4 asks that the tooltip stay readable at the chart's edges and flip
 *    rather than clip. Pinned to a corner it can never clip at all, which is the
 *    stronger version of the same guarantee: `side` flips it to the half the
 *    pointer is NOT in, so it never covers the crosshair either.
 * 3. On a 366-point series the pointer moves continuously while the day under it
 *    changes in steps. A bubble glued to the pointer jitters between two
 *    positions on a single day; a pinned one holds still and only its numbers
 *    change, which is what a reader is actually reading.
 *
 * The bubble is `pointer-events-none` so it can never eat the hover that
 * produced it. It is also `aria-hidden`: every chart that uses it carries its
 * values in real DOM text as well (a legend, a list, or the live region
 * `useNearestPoint` drives), so announcing the bubble too would read every
 * number twice.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { ReactNode } from "react";

export interface ChartTooltipRow {
  /** Stable per row — the series or bucket name. */
  key: string;
  label: string;
  /** Pre-formatted; the tooltip never decides how a number reads. */
  value: string;
  /** Colour key, usually a `SeriesSwatch`. */
  swatch?: ReactNode;
}

export interface ChartTooltipProps {
  /** The day, bucket or category being described. */
  title: string;
  rows: readonly ChartTooltipRow[];
  /**
   * Which edge to pin to. Callers pass the side AWAY from the pointer, so the
   * bubble never sits on top of the thing it describes.
   */
  side: "left" | "right";
  /** Shown instead of the rows when every series is hidden. */
  emptyLabel?: string;
  /**
   * The row to emphasise — the series the pointer is nearest to.
   *
   * The bubble deliberately lists EVERY series for the focused point, because
   * "how many Identified against Pursuing on the 26th" is one question and a
   * per-line tooltip would make it three hovers. What that costs is the link
   * between the point under the cursor and the row describing it, and this prop
   * is that link: same numbers, one of them pointed at.
   *
   * Absent when the focus came from the keyboard — there is no pointer, so
   * there is no nearest.
   */
  highlightKey?: string;
  testId: string;
}

export function ChartTooltip({
  title,
  rows,
  side,
  emptyLabel,
  highlightKey,
  testId,
}: ChartTooltipProps) {
  return (
    <div
      data-testid={testId}
      data-side={side}
      aria-hidden="true"
      className={`pointer-events-none absolute top-1 z-10 max-w-[14rem] rounded-sm border border-border bg-bg px-2.5 py-1.5 text-caption leading-snug text-text shadow-md ${
        side === "left" ? "left-1" : "right-1"
      }`}
    >
      <p className="font-semibold text-text">{title}</p>
      {rows.length === 0 ? (
        <p className="mt-0.5 text-text-secondary">{emptyLabel ?? "No series shown"}</p>
      ) : (
        <ul className="mt-0.5 flex flex-col gap-0.5">
          {rows.map((row) => {
            const highlighted = row.key === highlightKey;
            return (
              <li
                key={row.key}
                data-testid={highlighted ? `${testId}-highlighted` : undefined}
                className={`flex items-center gap-1.5 whitespace-nowrap ${
                  highlighted ? "-mx-1 rounded-xs bg-surface-raised px-1" : ""
                }`}
              >
                {row.swatch}
                <span className={highlighted ? "font-semibold text-text" : "text-text-secondary"}>
                  {row.label}
                </span>
                <strong className="ml-auto text-text">{row.value}</strong>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
